import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { normalizeHttpFailure, SentryHttpErrorReporter } from '@http-helpers/sentry-http-error';
import { resolveHttpReportAction } from '@http-helpers/sentry-http-policy';
import { NetworkStatusService } from '../../network/network-status.service';
import { createApplicationHttpError } from './application-http-error';
import {
  BYPASS_APPLICATION_HTTP_ERROR,
  EXPECTED_HTTP_ERROR_STATUSES,
} from './application-http-error-context';

export const applicationHttpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(BYPASS_APPLICATION_HTTP_ERROR)) return next(req);

  const sentryHttpErrorReporter = inject(SentryHttpErrorReporter);
  const network = inject(NetworkStatusService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }

      // e.g. a JSON parse error on 200: left for the global error handler, as before
      if (err.status !== 0 && err.status < 400) {
        return throwError(() => createApplicationHttpError(err, false));
      }

      const { action, reason } = resolveHttpReportAction(
        normalizeHttpFailure(err, req, 'http-interceptor'),
        {
          expectedStatuses: req.context.get(EXPECTED_HTTP_ERROR_STATUSES),
          online: network.online() && navigator.onLine,
        }
      );

      if (action === 'report') {
        sentryHttpErrorReporter.capture(err, req, 'http-interceptor');
      } else if (action === 'breadcrumb') {
        sentryHttpErrorReporter.breadcrumb(err, req, reason);
      }

      // Not reported failures are marked too, so the global error handler drops them as well
      return throwError(() => createApplicationHttpError(err, true));
    })
  );
};
